/**
 * User Lookup Service (cached).
 *
 * Resolves userMasterId -> display name for audit fields (createdBy / updatedBy are
 * stored as user_master ids). Backed by GET /api/v1/users/names-minimal and cached
 * in memory, mirroring personnel-lookup.service.
 */
import { api } from './api';

interface UserNameMinimal {
  _id: string;
  name: string;
}

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

let cache: Map<string, string> | null = null;
let cacheTimestamp = 0;
let fetchPromise: Promise<Map<string, string>> | null = null;

const fetchList = async (): Promise<UserNameMinimal[]> => {
  const res = await api.get<ApiResponse<UserNameMinimal[]>>('/api/v1/users/names-minimal');
  return res.data.data || [];
};

const getOrFetch = async (): Promise<Map<string, string>> => {
  if (cache && Date.now() - cacheTimestamp < CACHE_TTL) return cache;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const list = await fetchList();
      const map = new Map<string, string>();
      list.forEach((u) => {
        if (u._id) map.set(u._id, u.name || '');
      });
      cache = map;
      cacheTimestamp = Date.now();
      return map;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const userLookupService = {
  /** Map of userMasterId -> display name (only ids that resolve are included, so the
   *  caller can fall back to '-' for anything unresolved). */
  getNamesByIds: async (ids: (string | null | undefined)[]): Promise<Map<string, string>> => {
    const result = new Map<string, string>();
    const valid = ids.filter((id): id is string => !!id);
    if (valid.length === 0) return result;

    try {
      const map = await getOrFetch();
      valid.forEach((id) => {
        const name = map.get(id);
        if (name) result.set(id, name);
      });
    } catch {
      // Leave unresolved — the caller shows '-'.
    }
    return result;
  },

  clearCache: (): void => {
    cache = null;
    cacheTimestamp = 0;
    fetchPromise = null;
  },
};
